import type { Point, ScreenMapping } from './types';

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The image could not be loaded.'));
    image.src = src;
  });
}

export async function readImageFile(file: File): Promise<{ image: HTMLImageElement; url: string }> {
  const url = URL.createObjectURL(file);
  try {
    return { image: await loadImage(url), url };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

function sourceDimensions(source: CanvasImageSource) {
  if (source instanceof HTMLImageElement) return { width: source.naturalWidth, height: source.naturalHeight };
  if (source instanceof HTMLCanvasElement || source instanceof OffscreenCanvas) return { width: source.width, height: source.height };
  const sized = source as unknown as { width?: number; height?: number; displayWidth?: number; displayHeight?: number };
  return { width: Number(sized.width ?? sized.displayWidth ?? 1), height: Number(sized.height ?? sized.displayHeight ?? 1) };
}

function affineForTriangle(source: [Point, Point, Point], target: [Point, Point, Point]) {
  const [s0, s1, s2] = source;
  const [d0, d1, d2] = target;
  const det = s0.x * (s1.y - s2.y) + s1.x * (s2.y - s0.y) + s2.x * (s0.y - s1.y);
  if (Math.abs(det) < 0.00001) return null;
  const a = (d0.x * (s1.y - s2.y) + d1.x * (s2.y - s0.y) + d2.x * (s0.y - s1.y)) / det;
  const c = (d0.x * (s2.x - s1.x) + d1.x * (s0.x - s2.x) + d2.x * (s1.x - s0.x)) / det;
  const e = (d0.x * (s1.x * s2.y - s2.x * s1.y) + d1.x * (s2.x * s0.y - s0.x * s2.y) + d2.x * (s0.x * s1.y - s1.x * s0.y)) / det;
  const b = (d0.y * (s1.y - s2.y) + d1.y * (s2.y - s0.y) + d2.y * (s0.y - s1.y)) / det;
  const d = (d0.y * (s2.x - s1.x) + d1.y * (s0.x - s2.x) + d2.y * (s1.x - s0.x)) / det;
  const f = (d0.y * (s1.x * s2.y - s2.x * s1.y) + d1.y * (s2.x * s0.y - s0.x * s2.y) + d2.y * (s0.x * s1.y - s1.x * s0.y)) / det;
  return { a, b, c, d, e, f };
}

export function projectivePoint(corners: ScreenMapping['corners'], u: number, v: number): Point {
  const [p0, p1, p2, p3] = corners;
  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dx3 = p0.x - p1.x + p2.x - p3.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;
  const dy3 = p0.y - p1.y + p2.y - p3.y;
  const determinant = dx1 * dy2 - dx2 * dy1;
  let g = 0;
  let h = 0;
  if (Math.abs(determinant) > 0.000001) {
    g = (dx3 * dy2 - dx2 * dy3) / determinant;
    h = (dx1 * dy3 - dx3 * dy1) / determinant;
  }
  const a = p1.x - p0.x + g * p1.x;
  const b = p3.x - p0.x + h * p3.x;
  const d = p1.y - p0.y + g * p1.y;
  const e = p3.y - p0.y + h * p3.y;
  const denominator = g * u + h * v + 1;
  return {
    x: (a * u + b * v + p0.x) / denominator,
    y: (d * u + e * v + p0.y) / denominator,
  };
}

export function expandCorners(corners: ScreenMapping['corners'], bleed = 0.006): ScreenMapping['corners'] {
  if (!bleed) return corners;
  const centre = corners.reduce((sum, point) => ({ x: sum.x + point.x / 4, y: sum.y + point.y / 4 }), { x: 0, y: 0 });
  return corners.map((point) => ({
    x: centre.x + (point.x - centre.x) * (1 + bleed),
    y: centre.y + (point.y - centre.y) * (1 + bleed),
  })) as ScreenMapping['corners'];
}

function drawTriangle(
  ctx: CanvasRenderingContext2D,
  creative: CanvasImageSource,
  source: [Point, Point, Point],
  target: [Point, Point, Point],
) {
  const transform = affineForTriangle(source, target);
  if (!transform) return;
  // Canvas clips can leave hairline gaps between adjacent perspective triangles.
  // Slightly overlap only the clip (not the transform) so white creatives remain solid.
  const centre = target.reduce((sum, point) => ({ x: sum.x + point.x / 3, y: sum.y + point.y / 3 }), { x: 0, y: 0 });
  const clipTarget = target.map((point) => {
    const dx = point.x - centre.x;
    const dy = point.y - centre.y;
    const length = Math.hypot(dx, dy) || 1;
    return { x: point.x + (dx / length) * 0.75, y: point.y + (dy / length) * 0.75 };
  }) as [Point, Point, Point];
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(clipTarget[0].x, clipTarget[0].y);
  ctx.lineTo(clipTarget[1].x, clipTarget[1].y);
  ctx.lineTo(clipTarget[2].x, clipTarget[2].y);
  ctx.closePath();
  ctx.clip();
  ctx.setTransform(transform.a, transform.b, transform.c, transform.d, transform.e, transform.f);
  ctx.drawImage(creative, 0, 0);
  ctx.restore();
}

function invertMatrix3(matrix: number[]) {
  const [a, b, c, d, e, f, g, h, i] = matrix;
  const A = e * i - f * h;
  const B = f * g - d * i;
  const C = d * h - e * g;
  const determinant = a * A + b * B + c * C;
  if (Math.abs(determinant) < 0.0000001) return null;
  return [
    A / determinant, (c * h - b * i) / determinant, (b * f - c * e) / determinant,
    B / determinant, (a * i - c * g) / determinant, (c * d - a * f) / determinant,
    C / determinant, (b * g - a * h) / determinant, (a * e - b * d) / determinant,
  ];
}

function inverseProjectiveMatrix(corners: ScreenMapping['corners']) {
  const [p0, p1, p2, p3] = corners;
  const dx1 = p1.x - p2.x;
  const dx2 = p3.x - p2.x;
  const dx3 = p0.x - p1.x + p2.x - p3.x;
  const dy1 = p1.y - p2.y;
  const dy2 = p3.y - p2.y;
  const dy3 = p0.y - p1.y + p2.y - p3.y;
  const determinant = dx1 * dy2 - dx2 * dy1;
  let g = 0;
  let h = 0;
  if (Math.abs(determinant) > 0.000001) {
    g = (dx3 * dy2 - dx2 * dy3) / determinant;
    h = (dx1 * dy3 - dx3 * dy1) / determinant;
  }
  return invertMatrix3([
    p1.x - p0.x + g * p1.x, p3.x - p0.x + h * p3.x, p0.x,
    p1.y - p0.y + g * p1.y, p3.y - p0.y + h * p3.y, p0.y,
    g, h, 1,
  ]);
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

type ProjectiveSurface = { canvas: HTMLCanvasElement; x: number; y: number };

function surfaceGeometry(corners: ScreenMapping['corners'], margin = 2) {
  const bounds = quadBounds(corners);
  const x = Math.floor(bounds.left) - margin;
  const y = Math.floor(bounds.top) - margin;
  const width = Math.max(2, Math.ceil(bounds.left + bounds.width) - x + margin);
  const height = Math.max(2, Math.ceil(bounds.top + bounds.height) - y + margin);
  const localCorners = corners.map((point) => ({ x: point.x - x, y: point.y - y })) as ScreenMapping['corners'];
  return { x, y, width, height, localCorners };
}

function renderProjectiveWebGL(creative: HTMLCanvasElement, corners: ScreenMapping['corners']): ProjectiveSurface | null {
  const geometry = surfaceGeometry(corners);
  const inverse = inverseProjectiveMatrix(geometry.localCorners);
  if (!inverse) return null;
  const canvas = document.createElement('canvas');
  canvas.width = geometry.width;
  canvas.height = geometry.height;
  const gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: true });
  if (!gl) return null;
  const vertex = compileShader(gl, gl.VERTEX_SHADER, `
    attribute vec2 a_position;
    void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
  `);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, `
    precision highp float;
    uniform sampler2D u_texture;
    uniform mat3 u_inverse;
    uniform vec2 u_resolution;
    uniform vec2 u_texel;
    uniform vec2 u_texture_size;
    vec4 cubic(float value) {
      vec4 n = vec4(1.0, 2.0, 3.0, 4.0) - value;
      vec4 s = n * n * n;
      float x = s.x;
      float y = s.y - 4.0 * s.x;
      float z = s.z - 4.0 * s.y + 6.0 * s.x;
      float w = 6.0 - x - y - z;
      return vec4(x, y, z, w) / 6.0;
    }
    vec4 sampleBicubic(vec2 uv) {
      vec2 pixel = uv * u_texture_size - 0.5;
      vec2 fraction = fract(pixel);
      pixel -= fraction;
      vec4 xcubic = cubic(fraction.x);
      vec4 ycubic = cubic(fraction.y);
      vec4 centre = vec4(pixel.x - 0.5, pixel.x + 1.5, pixel.y - 0.5, pixel.y + 1.5);
      vec4 weights = vec4(
        xcubic.x + xcubic.y,
        xcubic.z + xcubic.w,
        ycubic.x + ycubic.y,
        ycubic.z + ycubic.w
      );
      vec4 offset = centre + vec4(xcubic.y, xcubic.w, ycubic.y, ycubic.w) / weights;
      offset /= u_texture_size.xxyy;
      vec4 sample0 = texture2D(u_texture, offset.xz);
      vec4 sample1 = texture2D(u_texture, offset.yz);
      vec4 sample2 = texture2D(u_texture, offset.xw);
      vec4 sample3 = texture2D(u_texture, offset.yw);
      float sx = weights.x / (weights.x + weights.y);
      float sy = weights.z / (weights.z + weights.w);
      return mix(mix(sample3, sample2, sx), mix(sample1, sample0, sx), sy);
    }
    void main() {
      vec2 destination = vec2(gl_FragCoord.x, u_resolution.y - gl_FragCoord.y);
      vec3 mapped = u_inverse * vec3(destination, 1.0);
      vec2 uv = mapped.xy / mapped.z;
      if (mapped.z <= 0.0 || uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) discard;
      vec4 colour = sampleBicubic(uv);
      vec3 neighbours = (
        texture2D(u_texture, uv + vec2(u_texel.x, 0.0)).rgb +
        texture2D(u_texture, uv - vec2(u_texel.x, 0.0)).rgb +
        texture2D(u_texture, uv + vec2(0.0, u_texel.y)).rgb +
        texture2D(u_texture, uv - vec2(0.0, u_texel.y)).rgb
      ) * 0.25;
      colour.rgb = clamp(colour.rgb + (colour.rgb - neighbours) * 0.14, 0.0, 1.0);
      gl_FragColor = colour;
    }
  `);
  if (!vertex || !fragment) return null;
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  // Canvas rows already align with the top-origin UVs produced by our inverse
  // homography. Flipping during upload would turn every screen upside down.
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, creative);

  // WebGL expects column-major matrices.
  gl.uniformMatrix3fv(gl.getUniformLocation(program, 'u_inverse'), false, new Float32Array([
    inverse[0], inverse[3], inverse[6],
    inverse[1], inverse[4], inverse[7],
    inverse[2], inverse[5], inverse[8],
  ]));
  gl.uniform2f(gl.getUniformLocation(program, 'u_resolution'), geometry.width, geometry.height);
  gl.uniform2f(gl.getUniformLocation(program, 'u_texel'), 1 / creative.width, 1 / creative.height);
  gl.uniform2f(gl.getUniformLocation(program, 'u_texture_size'), creative.width, creative.height);
  gl.viewport(0, 0, geometry.width, geometry.height);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.flush();
  return { canvas, x: geometry.x, y: geometry.y };
}

function renderProjectiveFallback(creative: HTMLCanvasElement, corners: ScreenMapping['corners']): ProjectiveSurface {
  const geometry = surfaceGeometry(corners);
  const canvas = document.createElement('canvas');
  canvas.width = geometry.width;
  canvas.height = geometry.height;
  const context = canvas.getContext('2d');
  if (!context) return { canvas, x: geometry.x, y: geometry.y };
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  const columns = 16;
  const rows = 28;
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const u0 = column / columns;
      const u1 = (column + 1) / columns;
      const v0 = row / rows;
      const v1 = (row + 1) / rows;
      const s00 = { x: u0 * creative.width, y: v0 * creative.height };
      const s10 = { x: u1 * creative.width, y: v0 * creative.height };
      const s11 = { x: u1 * creative.width, y: v1 * creative.height };
      const s01 = { x: u0 * creative.width, y: v1 * creative.height };
      const d00 = projectivePoint(geometry.localCorners, u0, v0);
      const d10 = projectivePoint(geometry.localCorners, u1, v0);
      const d11 = projectivePoint(geometry.localCorners, u1, v1);
      const d01 = projectivePoint(geometry.localCorners, u0, v1);
      drawTriangle(context, creative, [s00, s10, s11], [d00, d10, d11]);
      drawTriangle(context, creative, [s00, s11, s01], [d00, d11, d01]);
    }
  }
  return { canvas, x: geometry.x, y: geometry.y };
}

function traceQuad(ctx: CanvasRenderingContext2D, corners: ScreenMapping['corners']) {
  ctx.beginPath();
  corners.forEach((point, index) => index ? ctx.lineTo(point.x, point.y) : ctx.moveTo(point.x, point.y));
  ctx.closePath();
}

function quadBounds(corners: ScreenMapping['corners']) {
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return {
    left,
    top,
    width: Math.max(1, Math.max(...xs) - left),
    height: Math.max(1, Math.max(...ys) - top),
  };
}

function removeOldReflection(
  ctx: CanvasRenderingContext2D,
  corners: ScreenMapping['corners'],
  width: number,
  height: number,
) {
  const bounds = quadBounds(corners);
  const feather = Math.max(4, Math.min(18, Math.min(bounds.width, bounds.height) * 0.022));
  const geometry = surfaceGeometry(corners, Math.ceil(feather * 2));
  const layer = document.createElement('canvas');
  layer.width = geometry.width;
  layer.height = geometry.height;
  const layerContext = layer.getContext('2d');
  if (!layerContext) return;
  layerContext.imageSmoothingEnabled = true;
  layerContext.imageSmoothingQuality = 'high';

  // Build a floor-only vertical colour field from a wide local area. For each
  // row we discard dark objects and blown highlights, then average the middle
  // tones. No neighbouring person or screen can be copied into the result.
  const sample = document.createElement('canvas');
  sample.width = 96;
  sample.height = 64;
  const sampleContext = sample.getContext('2d', { willReadFrequently: true });
  if (!sampleContext) return;
  const sampleLeft = Math.max(0, bounds.left - bounds.width);
  const sampleRight = Math.min(width, bounds.left + bounds.width * 2);
  sampleContext.drawImage(
    ctx.canvas,
    sampleLeft,
    bounds.top,
    Math.max(1, sampleRight - sampleLeft),
    bounds.height,
    0,
    0,
    sample.width,
    sample.height,
  );

  const texture = document.createElement('canvas');
  texture.width = 8;
  texture.height = sample.height;
  const textureContext = texture.getContext('2d');
  if (!textureContext) return;
  try {
    const pixels = sampleContext.getImageData(0, 0, sample.width, sample.height).data;
    for (let y = 0; y < sample.height; y += 1) {
      const row: Array<{ r: number; g: number; b: number; luminance: number }> = [];
      for (let x = 0; x < sample.width; x += 1) {
        const index = (y * sample.width + x) * 4;
        const r = pixels[index];
        const g = pixels[index + 1];
        const b = pixels[index + 2];
        row.push({ r, g, b, luminance: r * 0.2126 + g * 0.7152 + b * 0.0722 });
      }
      row.sort((a, b) => a.luminance - b.luminance);
      const middle = row.slice(Math.floor(row.length * 0.3), Math.ceil(row.length * 0.72));
      const colour = middle.reduce((sum, pixel) => ({ r: sum.r + pixel.r, g: sum.g + pixel.g, b: sum.b + pixel.b }), { r: 0, g: 0, b: 0 });
      const count = Math.max(1, middle.length);
      textureContext.fillStyle = `rgb(${Math.round(colour.r / count)} ${Math.round(colour.g / count)} ${Math.round(colour.b / count)})`;
      textureContext.fillRect(0, y, texture.width, 1);
    }
  } catch {
    textureContext.fillStyle = '#8d8b83';
    textureContext.fillRect(0, 0, texture.width, texture.height);
  }
  layerContext.drawImage(texture, bounds.left - geometry.x, bounds.top - geometry.y, bounds.width, bounds.height);

  const mask = document.createElement('canvas');
  mask.width = geometry.width;
  mask.height = geometry.height;
  const maskContext = mask.getContext('2d');
  if (!maskContext) return;
  maskContext.filter = `blur(${feather}px)`;
  maskContext.fillStyle = '#fff';
  traceQuad(maskContext, geometry.localCorners);
  maskContext.fill();
  layerContext.globalCompositeOperation = 'destination-in';
  layerContext.drawImage(mask, 0, 0);
  ctx.drawImage(layer, geometry.x, geometry.y);
}

export function prepareCreative(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  canvas = document.createElement('canvas'),
): HTMLCanvasElement {
  const nativeScale = Math.max(sourceWidth / 1080, sourceHeight / 1920);
  const scale = Math.max(1 / 16, Math.min(nativeScale, 4096 / 1920));
  const targetWidth = Math.max(68, Math.round(1080 * scale));
  const targetHeight = Math.max(120, Math.round(1920 * scale));
  if (canvas.width !== targetWidth) canvas.width = targetWidth;
  if (canvas.height !== targetHeight) canvas.height = targetHeight;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Canvas rendering is not supported in this browser.');
  ctx.fillStyle = '#080b10';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const fitScale = Math.min(canvas.width / sourceWidth, canvas.height / sourceHeight);
  const width = sourceWidth * fitScale;
  const height = sourceHeight * fitScale;
  ctx.drawImage(source, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
  return canvas;
}

export function renderMockupFrame(
  output: HTMLCanvasElement,
  venue: CanvasImageSource,
  creative: HTMLCanvasElement,
  screens: ScreenMapping[],
  maxDimension = Number.POSITIVE_INFINITY,
  allowUpscale = false,
): HTMLCanvasElement {
  const venueSize = sourceDimensions(venue);
  const requestedScale = Number.isFinite(maxDimension)
    ? maxDimension / Math.max(venueSize.width, venueSize.height)
    : 1;
  const outputScale = allowUpscale ? Math.max(1, requestedScale) : Math.min(1, requestedScale);
  let width = Math.max(1, Math.round(venueSize.width * outputScale));
  let height = Math.max(1, Math.round(venueSize.height * outputScale));
  if (Number.isFinite(maxDimension)) {
    width = Math.max(2, width - (width % 2));
    height = Math.max(2, height - (height % 2));
  }
  if (output.width !== width) output.width = width;
  if (output.height !== height) output.height = height;
  const ctx = output.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Canvas rendering is not supported in this browser.');
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.filter = 'none';
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(venue, 0, 0, width, height);

  for (const screen of screens) {
    const scaledCorners = expandCorners(screen.corners, screen.bleed ?? 0.006)
      .map((point) => ({ x: point.x * outputScale, y: point.y * outputScale })) as ScreenMapping['corners'];
    const overlay = renderProjectiveWebGL(creative, scaledCorners)
      ?? renderProjectiveFallback(creative, scaledCorners);
    const bounds = quadBounds(scaledCorners);

    // The artwork itself remains fully opaque. Tonal adjustment is applied to
    // the finished surface, never by revealing the old advert underneath it.
    ctx.save();
    traceQuad(ctx, scaledCorners);
    ctx.clip();
    ctx.globalAlpha = 1;
    ctx.filter = 'brightness(0.94) contrast(0.97) saturate(0.93)';
    ctx.drawImage(overlay.canvas, overlay.x, overlay.y);
    ctx.restore();

    // Keep only a restrained inset edge. No glare, sheen or reflection is added.
    ctx.save();
    traceQuad(ctx, scaledCorners);
    ctx.clip();
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(1, Math.min(4, Math.min(bounds.width, bounds.height) * 0.012));
    ctx.strokeStyle = 'rgba(4,8,13,0.28)';
    traceQuad(ctx, scaledCorners);
    ctx.stroke();
    ctx.restore();

    if (screen.reflection) {
      const removalCorners = screen.reflection.corners
        .map((point) => ({ x: point.x * outputScale, y: point.y * outputScale })) as ScreenMapping['corners'];
      removeOldReflection(ctx, removalCorners, width, height);
    }

  }
  return output;
}

export async function renderMockup(
  venue: HTMLImageElement,
  creative: HTMLCanvasElement,
  screens: ScreenMapping[],
  maxDimension = Number.POSITIVE_INFINITY,
  allowUpscale = false,
): Promise<HTMLCanvasElement> {
  const output = renderMockupFrame(document.createElement('canvas'), venue, creative, screens, maxDimension, allowUpscale);
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  return output;
}

export function recommendedExportLongEdge(
  venueWidth: number,
  venueHeight: number,
  creative: HTMLCanvasElement,
  screens: ScreenMapping[],
  cap = 12000,
) {
  const distance = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);
  let requiredScale = 1;
  for (const screen of screens) {
    const screenWidth = Math.max(
      distance(screen.corners[0], screen.corners[1]),
      distance(screen.corners[3], screen.corners[2]),
    );
    const screenHeight = Math.max(
      distance(screen.corners[0], screen.corners[3]),
      distance(screen.corners[1], screen.corners[2]),
    );
    requiredScale = Math.max(requiredScale, creative.width / screenWidth, creative.height / screenHeight);
  }
  const nativeLongEdge = Math.max(venueWidth, venueHeight);
  return Math.round(Math.max(nativeLongEdge, Math.min(cap, nativeLongEdge * requiredScale)));
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/png', quality = 0.96): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error('The browser could not create the image file.')),
    type,
    quality,
  ));
}

function resizeForExport(source: HTMLCanvasElement, longEdge: number) {
  const scale = Math.min(1, longEdge / Math.max(source.width, source.height));
  if (scale === 1) return source;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(2, Math.round(source.width * scale));
  canvas.height = Math.max(2, Math.round(source.height * scale));
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Canvas rendering is not supported in this browser.');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Produces the highest-quality presentation JPEG that fits within the byte limit. */
export async function canvasToPresentationJpeg(canvas: HTMLCanvasElement, maxBytes = 1_000_000): Promise<Blob> {
  const sourceLongEdge = Math.max(canvas.width, canvas.height);
  let targetLongEdge = Math.min(sourceLongEdge, 6000);

  for (let resizeAttempt = 0; resizeAttempt < 8; resizeAttempt += 1) {
    const working = resizeForExport(canvas, targetLongEdge);
    const release = () => {
      if (working !== canvas) {
        working.width = 1;
        working.height = 1;
      }
    };

    const highest = await canvasToBlob(working, 'image/jpeg', 0.94);
    if (highest.size <= maxBytes) {
      release();
      return highest;
    }

    const minimumPreferred = await canvasToBlob(working, 'image/jpeg', 0.62);
    if (minimumPreferred.size <= maxBytes) {
      let best = minimumPreferred;
      let low = 0.62;
      let high = 0.94;
      for (let qualityAttempt = 0; qualityAttempt < 7; qualityAttempt += 1) {
        const quality = (low + high) / 2;
        const candidate = await canvasToBlob(working, 'image/jpeg', quality);
        if (candidate.size <= maxBytes) {
          best = candidate;
          low = quality;
        } else {
          high = quality;
        }
      }
      release();
      return best;
    }

    const estimatedScale = Math.sqrt(maxBytes / minimumPreferred.size) * 0.96;
    const nextLongEdge = Math.max(1600, Math.floor(targetLongEdge * Math.max(0.68, Math.min(0.9, estimatedScale))));
    release();
    if (nextLongEdge >= targetLongEdge) break;
    targetLongEdge = nextLongEdge;
  }

  const fallback = resizeForExport(canvas, Math.min(1600, sourceLongEdge));
  let blob = await canvasToBlob(fallback, 'image/jpeg', 0.58);
  for (let quality = 0.54; blob.size > maxBytes && quality >= 0.4; quality -= 0.04) {
    blob = await canvasToBlob(fallback, 'image/jpeg', quality);
  }
  if (fallback !== canvas) {
    fallback.width = 1;
    fallback.height = 1;
  }
  return blob;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
